import { SchemaObject } from 'ajv';

import {
  bool,
  int,
  nullable,
  nullType,
  num,
  ref,
  SCHEMA_BASE,
  stringList,
} from './common';

const identity = {
  reportUID: ref('uuid'),
  client_name: ref('clientName'),
  project_name: ref('projectName'),
  target_group_name: ref('targetGroupName'),
  workspace_id: ref('workspaceId'),
  job_definition: ref('jobDefinition'),
  requestedBy: ref('requestedBy'),
  numCores: ref('numCores'),
};

const demographics = {
  countries: ref('countryList'),
  age_ranges: ref('ageRanges'),
  include_female: ref('fraction'),
  include_male: ref('fraction'),
  SansRejector: bool,
};

/**
 * Market survey contract.
 * Every property the form sends is listed so unexpected keys are rejected.
 */
export const marketSurveySchema: SchemaObject = {
  $id: `${SCHEMA_BASE}market-survey.json`,
  title: 'Market survey report params',
  type: 'object',
  additionalProperties: false,
  complementaryFractions: ['include_female', 'include_male'],
  required: [
    ...Object.keys(identity),
    ...Object.keys(demographics),
    'report_type',
    'client_allowed_workspaces',
    'competitive_set_products',
    'competitive_set_folders',
    'smoking_habits',
    'experience_range',
    'constraint_levels',
    'n_sims',
  ],
  properties: {
    ...identity,
    ...demographics,
    report_type: { const: 'market_survey' },
    client_allowed_workspaces: ref('workspaceList'),
    competitive_set_products: ref('productList'),
    competitive_set_folders: {
      allOf: [ref('categoryList'), { type: 'array', minItems: 1 }],
    },
    custom_class_db_name: ref('customClassDbName'),
    custom_class_db_products: stringList(),
    usa_region: nullable({ type: 'string' }),
    clts: stringList({ maxItems: 20 }),
    smoking_habits: ref('smokingHabits'),
    asian: bool,
    black: bool,
    hispanic: bool,
    white: bool,
    experience_range: {
      type: 'array',
      items: { enum: [1, 2, 3] },
      minItems: 1,
      uniqueItems: true,
    },
    constraint_levels: {
      type: 'array',
      items: ref('constraintLevel'),
      minItems: 1,
      uniqueItems: true,
    },
    n_sims: {
      type: 'array',
      items: int(1, 10000),
      minItems: 1,
      uniqueItems: true,
    },
    arch_age_start: { type: 'array', items: int(0, 99) },

    profilingEnabled: bool,
    generate_ppt: bool,
    include_texture: bool,
    texture_only: bool,
    texture_data_only: bool,
    showConfidence: bool,
    show_TC_se: bool,
    include_archetypes: bool,
    plotNorm: bool,
    combine_artificial_sweeteners: bool,
    wipe_out_imputed_ggvars: bool,
    bootstrap_pq: bool,
    includeNoveltyScores: bool,
    only_include_parents: bool,
    experimental_features: bool,
    use_hyperlocal_glp: bool,
    simulate_pq_values: bool,
    report_id_to_inherit_parent_data_sources: ref('reportIdToInherit'),

    max_num_texture_allowed: nullable(int(0)),
    texture_weight_threshold_list: num(0),
    refresh_texture: bool,
    texture_community_path: nullable({ type: 'string' }),
    n_review_archetype: int(0),
    n_review_archetype_max: int(0),
    min_unique_reviewers: int(0),
    n_cluster_archetype: int(0),
    n_cluster_archetype_max: int(0),
    include_market_maps: bool,
    include_pairwise_comparison: bool,
    include_gradients: bool,
    apply_margEffect_ggvar_range: bool,
    maxRowSummaryTable: int(1),
    maxRowDecomSubTable: int(1),
    minReviews: int(0),
    alcoholLegal: bool,
    apply_doofus_factor: bool,
    new_reference_flavors_allowed: bool,
    is_opt_test_mode: bool,
    hot_patch_post_label_prop_data: bool,
    extrema_analysis: bool,
    selected_product_ids: nullType,
    product_rename: nullType,
    supplement_competitive_set_products: bool,
    competitive_set_base_components: nullType,
    competitive_set_other_components: nullType,
    competitive_set_components_categories_allowed: nullType,
    products_to_opt: nullType,
    parallelCode: bool,
    opt_file_to_load: nullType,
    concept_base_products: nullType,
    ref_flavors_to_explore: nullType,
    region: nullType,
    age_filtering_list: nullType,
    glp_s3_key: { type: 'string', minLength: 1 },
    glp_s3_bucket: { type: 'string', minLength: 1 },
    post_label_prop_data_path: { type: 'string' },
    latest_date: nullType,
    archetype_seed: nullType,
    demo_id_path: nullType,
    PQ_model_path: nullType,
    ref_flavor_path: nullType,
    archetype_PQ_model_path: nullType,
    archetype_list_path: nullType,
    explode_and_remove_refFlavors: nullType,
    pull_glp_from_s3: bool,
    show_pg_gNorm: bool,
    plotTextureGradients: bool,
    timestamp_start: nullable(int(0)),
    slack_me: bool,
  },
  allOf: [
    {
      if: {
        required: ['texture_data_only'],
        properties: { texture_data_only: { const: true } },
      },
      then: {
        properties: {
          texture_only: { const: true },
          include_texture: { const: true },
        },
      },
    },
    {
      if: {
        required: ['texture_only'],
        properties: { texture_only: { const: true } },
      },
      then: { properties: { include_texture: { const: true } } },
    },
    {
      if: {
        required: ['custom_class_db_name'],
        properties: { custom_class_db_name: ref('nonEmptyString') },
      },
      then: {
        required: ['custom_class_db_products'],
        properties: {
          custom_class_db_products: { type: 'array', minItems: 1 },
        },
      },
    },
  ],
};
